import { AbstractBuildstep } from "@project/emerge";
import {
  type DragConstraint,
  type DragUpdateResult,
  type SerializableVector3,
  Sides,
} from "@project/emerge-types";
import MakerJS from "makerjs";
import { Vector2, Vector3 } from "three";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { InteriorType } from "../enum/InteriorType";
import { LTPartType } from "../enum/PartType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { RackInteriorOptions } from "../interfaces/RackInteriorOptions";
import type { TrayPanelDefinition } from "../interfaces/TrayPanelProperties";
import { DefaultGroupName, type Rect } from "../structure/Rect";
import {
  ensureStrength,
  getDefaultPanelColor,
} from "./interior-helpers/InteriorHelpers";
import type { InteriorTrayPanelDependencyData } from "./interior-helpers/InteriorTypes";

const MILLING_EXTRA = 3;
const MIN_COMPARTMENT_SIZE = 50; // Minimum size for a compartment

interface RackCutout {
  cut: number;
  path: string;
  side: Sides;
  position: Vector3;
  orientation: number;
}

interface VerticalSeparatorCutoutContext {
  rowIndex: number;
  rowCount: number;
  x: number;
  trayStrength: number;
  deltaW: number;
  profileHeight: number;
  extra: number;
  strength: number;
}

// Extended dependency data with drag info
interface RackTrayPanelDependencyData extends InteriorTrayPanelDependencyData {
  dragType: "horizontal" | "vertical";
  rowIndex: number;
  colIndex?: number; // Only for vertical panels
  // Drag constraint data (pre-computed)
  dragConstraint?: DragConstraint;
  settingsContext?: Record<string, unknown>;
}

/**
 * RackInteriorBuildStep handles interior tray panels for RACK interior type:
 * - Horizontal divider panels between rows (draggable up/down)
 * - Vertical divider panels within rows (draggable left/right)
 *
 * This buildstep is used by advanced product types: SingleDoorCase, DoubleDoorCase, LCase
 */
export class RackInteriorBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  TrayPanelDefinition,
  RackTrayPanelDependencyData
> {
  constructor() {
    super("RackInteriorBuildStep");
  }

  private createRectPath(width: number, height: number): string {
    const shape = MakerJS.model.center(
      new MakerJS.models.Rectangle(width, height),
    );
    const pathData = MakerJS.exporter.toSVGPathData(shape, { origin: [0, 0] });
    return typeof pathData === "string" ? pathData : "";
  }

  private buildHorizontalDragConstraint(
    position: Vector3,
    rowIndex: number,
    heights: number[],
    trayStrength: number,
    innerDimensionsY: number,
  ): {
    dragConstraint: DragConstraint;
    settingsContext: Record<string, unknown>;
  } {
    // Calculate bounds for vertical movement
    const totalHeightAbove = heights
      .slice(0, rowIndex + 1)
      .reduce((sum, h) => sum + h, 0);
    const totalHeightBelow = heights
      .slice(rowIndex + 1)
      .reduce((sum, h) => sum + h, 0);

    const topY = innerDimensionsY / 2;

    // Min Y: can't go lower than leaving MIN_COMPARTMENT_SIZE for row below
    const minY =
      topY -
      totalHeightAbove -
      (totalHeightBelow - MIN_COMPARTMENT_SIZE) -
      (heights.length - rowIndex - 2) * trayStrength;
    // Max Y: can't go higher than leaving MIN_COMPARTMENT_SIZE for current row
    const maxY =
      topY -
      (totalHeightAbove - (heights[rowIndex] ?? 0) + MIN_COMPARTMENT_SIZE) -
      rowIndex * trayStrength;

    const dragConstraint: DragConstraint = {
      type: "edge",
      axis: [0, 1, 0], // Vertical movement
      bounds: {
        min: [position.x, minY, position.z] as SerializableVector3,
        max: [position.x, maxY, position.z] as SerializableVector3,
      },
      snapAnchors: [],
      sourceOptionUuid: `rack_horizontal_${rowIndex}`,
    };

    const settingsContext = {
      dragType: "horizontal",
      rowIndex,
      topY,
      trayStrength,
      originalY: position.y,
    };

    return { dragConstraint, settingsContext };
  }

  private buildVerticalDragConstraint(
    position: Vector3,
    rowIndex: number,
    colIndex: number,
    widths: number[][],
    trayStrength: number,
    innerWidth: number,
  ): {
    dragConstraint: DragConstraint;
    settingsContext: Record<string, unknown>;
  } {
    const rowWidths = widths[rowIndex] ?? [];

    // Calculate bounds for horizontal movement
    const totalWidthLeft = rowWidths
      .slice(0, colIndex + 1)
      .reduce((sum, w) => sum + w, 0);
    const totalWidthRight = rowWidths
      .slice(colIndex + 1)
      .reduce((sum, w) => sum + w, 0);

    const leftX = -innerWidth / 2;

    // Min X: can't go further left than leaving MIN_COMPARTMENT_SIZE for left compartment
    const minX =
      leftX +
      (totalWidthLeft - (rowWidths[colIndex] ?? 0) + MIN_COMPARTMENT_SIZE) +
      colIndex * trayStrength;
    // Max X: can't go further right than leaving MIN_COMPARTMENT_SIZE for right compartment
    const maxX =
      leftX +
      totalWidthLeft +
      (totalWidthRight - MIN_COMPARTMENT_SIZE) +
      colIndex * trayStrength;

    const dragConstraint: DragConstraint = {
      type: "edge",
      axis: [1, 0, 0], // Horizontal movement
      bounds: {
        min: [minX, position.y, position.z] as SerializableVector3,
        max: [maxX, position.y, position.z] as SerializableVector3,
      },
      snapAnchors: [],
      sourceOptionUuid: `rack_vertical_${rowIndex}_${colIndex}`,
    };

    const settingsContext = {
      dragType: "vertical",
      rowIndex,
      colIndex,
      leftX,
      trayStrength,
      originalX: position.x,
    };

    return { dragConstraint, settingsContext };
  }

  private collectHorizontalDividerCutouts(
    blueprint: FlightCaseBlueprint,
    y: number,
    trayMaterial: { strength: number },
    deltaW: number,
    profileHeight: number,
    extra: number,
    strength: number,
  ): RackCutout[] {
    const cutouts: RackCutout[] = [];
    for (const horizontalSide of [Sides.LEFT, Sides.RIGHT]) {
      const rectKey = `${DefaultGroupName}/${Sides[horizontalSide]}`;
      const rect: Rect | undefined = blueprint.rectsById?.get(rectKey);
      if (!rect) continue;

      const { edges } = rect;

      for (const edgeIndex of [EdgeIndex.LEFT, EdgeIndex.RIGHT]) {
        const edge = edges[edgeIndex];
        if (edge?.type !== EdgeType.FROMCUT) continue;

        cutouts.push({
          cut: 0,
          orientation: 0,
          position: new Vector3(
            edge.start.outerPos.x,
            y + trayMaterial.strength / 2,
            edge.center.z,
          ),
          path: this.createRectPath(
            2 * deltaW,
            2 * (profileHeight + extra - strength + MILLING_EXTRA),
          ),
          side: horizontalSide,
        });

        for (const connector of edge.connectors) {
          const dist = Math.abs(
            connector.position.y - y + trayMaterial.strength / 2,
          );
          const connectorCut =
            (connector.partSelection as { cut?: number })?.cut ?? 0;

          if (connectorCut / 2 + trayMaterial.strength / 2 > dist) {
            cutouts.push({
              cut: 0,
              orientation: 0,
              position: new Vector3(
                edge.start.outerPos.x,
                y + trayMaterial.strength / 2,
                edge.center.z,
              ),
              path: this.createRectPath(2 * (9 + 6.5), 2 * 50),
              side: horizontalSide,
            });
          }
        }
      }
    }
    return cutouts;
  }

  private collectVerticalSeparatorCutouts(
    blueprint: FlightCaseBlueprint,
    context: VerticalSeparatorCutoutContext,
  ): RackCutout[] {
    const {
      rowIndex,
      rowCount,
      x,
      trayStrength,
      deltaW,
      profileHeight,
      extra,
      strength,
    } = context;
    const cutouts: RackCutout[] = [];
    if (rowIndex !== 0 && rowIndex !== rowCount - 1) {
      return cutouts;
    }

    // Top row → lid rect only, bottom row → floor rect only, single rack row → both
    // (pockets from the non-bounding face would be geometrically wrong).
    const includeFloorRect = rowIndex === rowCount - 1;
    const includeLidRect = rowIndex === 0;

    for (const verticalSide of [Sides.BOTTOM, Sides.TOP]) {
      if (verticalSide === Sides.BOTTOM && !includeFloorRect) continue;
      if (verticalSide === Sides.TOP && !includeLidRect) continue;

      const rectKey = `${DefaultGroupName}/${Sides[verticalSide]}`;
      const rect: Rect | undefined = blueprint.rectsById?.get(rectKey);
      if (!rect) continue;

      const { edges } = rect;

      for (const edgeIndex of [EdgeIndex.TOP, EdgeIndex.BOTTOM]) {
        const edge = edges[edgeIndex];
        if (edge?.type !== EdgeType.FROMCUT) continue;

        cutouts.push({
          cut: 0,
          orientation: 0,
          position: new Vector3(
            x + trayStrength / 2,
            edge.start.outerPos.y,
            edge.center.z,
          ),
          path: this.createRectPath(
            2 * (profileHeight + extra - strength + MILLING_EXTRA),
            2 * deltaW,
          ),
          side: verticalSide,
        });
      }
    }
    return cutouts;
  }

  private buildRackPanels(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    dependencies: Map<string, RackTrayPanelDependencyData>,
  ): void {
    const interior = settings.interiorOptions as RackInteriorOptions;
    const { heights, widths, trayMaterial } = interior;

    // Get side rect using rectsById like the old code
    const sideRectKey = `${DefaultGroupName}/${Sides[Sides.RIGHT]}`;
    const sideRect: Rect | undefined = blueprint.rectsById?.get(sideRectKey);

    if (!sideRect || !heights || !widths || !trayMaterial) {
      return;
    }

    const panelDepth = sideRect.innerSize.x;
    const panelZ = sideRect.center.z;

    const {
      gapWidth = 0,
      extra = 0,
      height: profileHeight = 0,
      strength = 0,
    } = settings.exteriorOptions.profiles ?? {};

    const deltaW = gapWidth + strength;
    const trayStrength = trayMaterial.strength;

    let y = blueprint.innerDimensions.y / 2;

    for (let h = 0; h < heights.length; h++) {
      const height = heights[h];
      if (height === undefined) continue;
      y -= height;

      if (h !== heights.length - 1) {
        const cutouts = this.collectHorizontalDividerCutouts(
          blueprint,
          y,
          trayMaterial,
          deltaW,
          profileHeight,
          extra,
          strength,
        );

        const panelId = `traypanel_rack_h_${h}`;
        const panelPosition = new Vector3(0, y - trayStrength / 2, panelZ);

        const { dragConstraint, settingsContext } =
          this.buildHorizontalDragConstraint(
            panelPosition,
            h,
            heights,
            trayStrength,
            blueprint.innerDimensions.y,
          );

        const dep: RackTrayPanelDependencyData = {
          partId: panelId,
          partKind: "InteriorTrayPanel",
          type: LTPartType.TrayPanel,
          group: DefaultGroupName,
          buildstepName: this.buildstepName,
          material: trayMaterial,
          color: getDefaultPanelColor(),
          position: panelPosition,
          side: Sides.BOTTOM,
          size: new Vector3(blueprint.innerDimensions.x, 0, panelDepth),
          orientation: 0,
          cutouts,
          dragType: "horizontal",
          rowIndex: h,
          dragConstraint,
          settingsContext,
        };
        dependencies.set(dep.partId, dep);
      }

      let x = -blueprint.innerDimensions.x / 2;
      const rowWidths = widths[h] ?? [];

      for (let w = 0; w < rowWidths.length; w++) {
        const segWidth = rowWidths[w];
        if (segWidth === undefined) continue;
        x += segWidth;

        if (w !== rowWidths.length - 1) {
          const cutouts = this.collectVerticalSeparatorCutouts(blueprint, {
            rowIndex: h,
            rowCount: heights.length,
            x,
            trayStrength,
            deltaW,
            profileHeight,
            extra,
            strength,
          });

          const panelId = `traypanel_rack_v_${h}_${w}`;
          const panelPosition = new Vector3(
            x + trayStrength / 2,
            y + height / 2,
            panelZ,
          );

          const { dragConstraint, settingsContext } =
            this.buildVerticalDragConstraint(
              panelPosition,
              h,
              w,
              widths,
              trayStrength,
              interior.width,
            );

          const dep: RackTrayPanelDependencyData = {
            partId: panelId,
            partKind: "InteriorTrayPanel",
            type: LTPartType.TrayPanel,
            group: DefaultGroupName,
            buildstepName: this.buildstepName,
            material: trayMaterial,
            color: getDefaultPanelColor(),
            position: panelPosition,
            side: Sides.RIGHT,
            size: new Vector3(panelDepth, 0, height),
            orientation: 0,
            cutouts,
            dragType: "vertical",
            rowIndex: h,
            colIndex: w,
            dragConstraint,
            settingsContext,
          };
          dependencies.set(dep.partId, dep);
        }
        x += trayStrength;
      }
      y -= trayStrength;
    }
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, RackTrayPanelDependencyData> {
    const dependencies = new Map<string, RackTrayPanelDependencyData>();

    // Only process RACK interior type
    if (settings.interiorOptions.type !== InteriorType.RACK) {
      return dependencies;
    }

    this.buildRackPanels(blueprint, settings, dependencies);

    return dependencies;
  }

  public createPart(
    partId: string,
    dep: RackTrayPanelDependencyData,
  ): TrayPanelDefinition {
    const trayPanelColor = dep.color ?? getDefaultPanelColor();
    const materialStrength = dep.material?.strength;
    const trayPanelStrength = ensureStrength(materialStrength, dep.material);

    const panelPart: TrayPanelDefinition = {
      id: partId,
      type: dep.type,
      group: dep.group,
      buildstepName: dep.buildstepName,
      color: trayPanelColor,
      position: dep.position.clone(),
      size: new Vector2(dep.size.x, dep.size.z),
      strength: trayPanelStrength,
      side: dep.side,
      rotation: this.rotateOntoSide(dep.side, dep.orientation),
      orientation: dep.orientation,
      cutouts: dep.cutouts ?? [],
      ...(dep.dragConstraint ? { dragConstraint: dep.dragConstraint } : {}),
      ...(dep.settingsContext ? { settingsContext: dep.settingsContext } : {}),
    };

    return panelPart;
  }

  /**
   * Handle drag updates for tray panels.
   * - Horizontal panels: adjust heights array
   * - Vertical panels: adjust widths array
   */
  public updateSettingsFromDrag(
    _partId: string,
    newPosition: Vector3,
    _constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!settingsContext) return null;

    const dragType = settingsContext.dragType as string;

    if (dragType === "horizontal") {
      return this.updateHorizontalDrag(
        newPosition,
        settingsContext,
        currentSettings,
      );
    } else if (dragType === "vertical") {
      return this.updateVerticalDrag(
        newPosition,
        settingsContext,
        currentSettings,
      );
    }

    return null;
  }

  private updateHorizontalDrag(
    newPosition: Vector3,
    context: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const rowIndex = context.rowIndex as number;
    const topY = context.topY as number;
    const trayStrength = context.trayStrength as number;

    const interior = currentSettings.interiorOptions as RackInteriorOptions;
    const heights = [...interior.heights];

    // Calculate new position along Y axis
    const newY = newPosition.y;

    // Calculate cumulative height up to this panel
    let cumulativeHeight = 0;
    for (let i = 0; i <= rowIndex; i++) {
      cumulativeHeight += heights[i] ?? 0;
      if (i < rowIndex) {
        cumulativeHeight += trayStrength;
      }
    }

    // New panel Y position relative to top
    const panelYFromTop = topY - newY - trayStrength / 2;

    // Calculate how much height to redistribute
    const currentPanelYFromTop =
      cumulativeHeight + rowIndex * trayStrength - trayStrength / 2;
    const deltaHeight = panelYFromTop - currentPanelYFromTop;

    // Adjust heights: increase current row, decrease next row
    const newCurrentHeight = Math.max(
      MIN_COMPARTMENT_SIZE,
      Math.round((heights[rowIndex] ?? 0) + deltaHeight),
    );
    const newNextHeight = Math.max(
      MIN_COMPARTMENT_SIZE,
      Math.round((heights[rowIndex + 1] ?? 0) - deltaHeight),
    );

    heights[rowIndex] = newCurrentHeight;
    heights[rowIndex + 1] = newNextHeight;

    const newInteriorOptions = {
      ...interior,
      heights,
    };

    return {
      settingsUpdate: {
        interiorOptions: newInteriorOptions,
      },
      displayValues: {
        primary: {
          label: `Row ${rowIndex + 1}`,
          value: newCurrentHeight,
          unit: "mm",
        },
        secondary: {
          label: `Row ${rowIndex + 2}`,
          value: newNextHeight,
          unit: "mm",
        },
      },
    };
  }

  private updateVerticalDrag(
    newPosition: Vector3,
    context: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const rowIndex = context.rowIndex as number;
    const colIndex = context.colIndex as number;
    const leftX = context.leftX as number;
    const trayStrength = context.trayStrength as number;

    const interior = currentSettings.interiorOptions as RackInteriorOptions;
    const widths = interior.widths.map((row) => [...row]);
    const rowWidths = widths[rowIndex];

    if (!rowWidths) return null;

    // Calculate new position along X axis
    const newX = newPosition.x;

    // Calculate cumulative width up to this panel
    let cumulativeWidth = 0;
    for (let i = 0; i <= colIndex; i++) {
      cumulativeWidth += rowWidths[i] ?? 0;
      if (i < colIndex) {
        cumulativeWidth += trayStrength;
      }
    }

    // New panel X position relative to left edge
    const panelXFromLeft = newX - leftX - trayStrength / 2;

    // Calculate how much width to redistribute
    const currentPanelXFromLeft =
      cumulativeWidth + colIndex * trayStrength - trayStrength / 2;
    const deltaWidth = panelXFromLeft - currentPanelXFromLeft;

    // Adjust widths: increase current column, decrease next column
    const newCurrentWidth = Math.max(
      MIN_COMPARTMENT_SIZE,
      Math.round((rowWidths[colIndex] ?? 0) + deltaWidth),
    );
    const newNextWidth = Math.max(
      MIN_COMPARTMENT_SIZE,
      Math.round((rowWidths[colIndex + 1] ?? 0) - deltaWidth),
    );

    rowWidths[colIndex] = newCurrentWidth;
    rowWidths[colIndex + 1] = newNextWidth;

    const newInteriorOptions = {
      ...interior,
      widths,
    };

    return {
      settingsUpdate: {
        interiorOptions: newInteriorOptions,
      },
      displayValues: {
        primary: {
          label: `Col ${colIndex + 1}`,
          value: newCurrentWidth,
          unit: "mm",
        },
        secondary: {
          label: `Col ${colIndex + 2}`,
          value: newNextWidth,
          unit: "mm",
        },
      },
    };
  }
}
